import { Link, useRouteLoaderData } from "@remix-run/react"
import dayjs from "dayjs"
import { cn } from "~/utils/shadecn"
import { isModernPricing, pricingSuffix, type PricingSuffix } from "~/utils/pricing"

// Subscribe-nudge headline, per pricing generation. The rule is that the copy must quote the price
// the VIEWER'S OWN cohort actually pays, so this is keyed by suffix rather than by a boolean:
//   -v2 → quickie-v2 is $20 for 5 credits
//   -v3 → quickie-v3 is $15 for 5 credits
// The v2 line previously read "Want 5 for $10?", which is the LEGACY quickie price ($10/5) sitting
// on the v2 branch by mistake — v2 users were quoted half what they would be charged.
//
// ""  → legacy. Left exactly as-is on purpose: legacy is the control arm of a live experiment and
// rewriting its copy mid-flight would corrupt the comparison. Note it does NOT match a real
// package — legacy pay-as-you-go is $5 for 1 credit, not $1 (prisma/seed.sql PackageID 18).
// That discrepancy predates this branch; changing it is a product decision, not a refactor.
const QUICKIE_PROMO: Record<PricingSuffix, string> = {
  "": "Want 1 for $1?",
  "-v2": "Want 5 for $20?",
  "-v3": "Want 5 for $15?",
}

export default function PromotionBanner() {
  const { user, sub } = useRouteLoaderData("root")
  // Two different questions — see ~/utils/pricing. isModern gates the annual upsell (a feature no
  // modern generation has); suffix picks copy whose prices differ per generation.
  const isModern = isModernPricing(user)
  const suffix = pricingSuffix(user)

  // Modern-pricing users: hide annual upsell (no modern generation has an annual plan)
  const hideAnnualUpsell = isModern && sub

  return (
    <div
      className={cn(
        "relative bg-primary/90 text-center text-zinc-50 max-w-4xl mx-auto py-1 px-2 md:py-3 md:px-4 mb-4 rounded-lg",
        (sub && sub.Package.RecurUnit === 12) || hideAnnualUpsell ? "hidden" : ""
      )}>
      {user ?
        <>
          {sub ?
            <Link to="/billing" className="text-balance hover:underline text-xs sm:text-sm">
              <b>Save up to $100</b>
              <br />
              Upgrade to annual now.
            </Link>
          : dayjs(user.CreatedAt) > dayjs().subtract(1, "month") ?
            <Link to="/subscribe" className="text-balance hover:underline text-xs sm:text-sm">
              <b>Gift For New Members</b>
              {/* hello-v2 and hello-v3 are both $12, so both modern generations share this string. */}
              <br />
              {isModern ?
                "A welcome bundle of credits. Watch any 3 videos for $12."
              : "A welcome bundle of credits. Watch any 3 videos for $7."}
            </Link>
          : <Link to="/subscribe" className="text-balance hover:underline text-xs sm:text-sm">
              <b>{QUICKIE_PROMO[suffix]}</b>
              <br />
              Subscribe today.
            </Link>
          }
        </>
      : <Link to="/register" className="text-balance hover:underline text-xs sm:text-sm">
          <b>Someone just shared something hot – Explore now.</b>
        </Link>
      }
    </div>
  )
}
